import { GraduationCap, type LucideIcon } from 'lucide-react';
import type { ProfileId } from './profileIdentity';

/** `icon` is optional: a row without one keeps the sidebar's default profile icon. */
export interface ProfileMenuItem { label: string; to: string; readOnly?: boolean; icon?: LucideIcon }

export interface ProfileMenuSection { label: string; items: ProfileMenuItem[] }

// 2026-10-02 App Tour: lessons for the sales team (P01 + P02), always the last section.
// 2026-10-03 (Manak): the admin (P18) gets it too — the phone setup and permission lessons are admin only.
const HELP_SECTION: ProfileMenuSection = { label: "Help", items: [
  {"label": "App Tour", "to": "/tour", icon: GraduationCap},
] };

// Locked spec 2026-09-15, amended 2026-09-16: personal sales entries only for P01/P02.
const MENUS: Record<ProfileId, ProfileMenuSection[]> = {
  P01: [
    { label: "Performance", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "My Scorecard", "to": "/scorecard"},
      {"label": "Team Scorecards", "to": "/scorecard/team"},
      {"label": "Pipeline", "to": "/pipeline"},
      {"label": "Sales Targets", "to": "/sales-targets"},
    ] },
    { label: "Sales", items: [
      {"label": "Leads", "to": "/leads"},
      {"label": "Proposals", "to": "/proposals"},
      {"label": "Quick Quote", "to": "/quick-quote"},
      {"label": "Orders", "to": "/sales-orders"},
      {"label": "Discount Approvals", "to": "/discount-requests"},
      {"label": "Contacts", "to": "/contacts"},
      {"label": "Activities", "to": "/sales/activities"},
    ] },
    { label: "Team", items: [
      {"label": "Reps", "to": "/sales/reps"},
      {"label": "Follow-ups", "to": "/admin/follow-ups"},
      {"label": "Meetings", "to": "/meetings"},
      {"label": "Team Tracking", "to": "/team-tracking"},
      {"label": "Attendance", "to": "/attendance-admin"},
    ] },
    // 2026-09-24 prospect-dealers spec §5 (supersedes the profile-round lock for
    // this row): Prospect dealers sits beside the dealers page.
    { label: "Dealers", items: [
      {"label": "My dealers", "to": "/dealer-management"},
      {"label": "Prospect dealers", "to": "/dealer-prospects"},
    ] },
    HELP_SECTION,
  ],
  P02: [
    { label: "My Day", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "Call Queue", "to": "/telecaller"},
      {"label": "Follow-ups", "to": "/follow-ups"},
      {"label": "My Scorecard", "to": "/scorecard"},
    ] },
    { label: "Sales", items: [
      {"label": "My Leads", "to": "/leads"},
      {"label": "Quick Quote", "to": "/quick-quote"},
      {"label": "Proposals", "to": "/proposals"},
      {"label": "Discount Requests", "to": "/discount-requests"},
      {"label": "My Orders", "to": "/sales-orders"},
      {"label": "Activities", "to": "/sales/activities"},
      {"label": "Contacts", "to": "/contacts"},
    ] },
    { label: "Money", items: [
      {"label": "My Outstanding", "to": "/my-outstanding"},
    ] },
    // 2026-09-24 prospect-dealers mockup panel 4, approved by Manak (supersedes
    // the older D6 lock): the coordinator works her MANAGER's dealers and
    // prospects. This menu row is also what admits /dealer-management for P02
    // at the profile layer — the page itself acts via onBehalfOfUserId.
    { label: "Dealers", items: [
      {"label": "My dealers", "to": "/dealer-management"},
      {"label": "Prospect dealers", "to": "/dealer-prospects"},
    ] },
    HELP_SECTION,
  ],
  P03: [
    { label: "Inbox", items: [
      {"label": "Lead Triage", "to": "/leads/triage"},
      {"label": "Dealers", "to": "/dealer-management"},
      {"label": "Prospect dealers", "to": "/dealer-prospects"},
    ] },
    { label: "Performance", items: [
      {"label": "Lead Ops", "to": "/lead-ops"},
    ] },
    { label: "You", items: [
      {"label": "Profile", "to": "/profile"},
    ] },
  ],
  P04: [
    { label: "CRM", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "Contacts", "to": "/contacts"},
      {"label": "Customers", "to": "/customers"},
      {"label": "Leads", "to": "/leads"},
      {"label": "Callbacks", "to": "/crm/callbacks"},
      {"label": "Data Quality", "to": "/crm/data-quality"},
      {"label": "Activities", "to": "/sales/activities"},
    ] },
    { label: "Team", items: [
      {"label": "Access Requests", "to": "/admin/access-requests"},
      {"label": "Discount Requests", "to": "/discount-requests"},
      {"label": "Team Report Card", "to": "/admin/employee-report"},
      {"label": "Team Tracking", "to": "/team-tracking"},
    ] },
    { label: "View only", items: [
      {"label": "Dispatch Status", "to": "/dispatch-status", "readOnly": true},
    ] },
  ],
  P05: [
    { label: "Orders", items: [
      {"label": "Dashboard", "to": "/purchase-dashboard"},
      {"label": "Purchase Requests", "to": "/purchase-requests"},
      {"label": "RFQs / Quotations", "to": "/quotation-jobs"},
      {"label": "POs", "to": "/purchase-orders"},
      {"label": "SO → PO Mapping", "to": "/so-po-mapping"},
    ] },
    { label: "Vendors", items: [
      {"label": "Vendors", "to": "/vendors"},
      {"label": "Rate Comparison", "to": "/vendor-rate-comparison"},
      {"label": "Job-work Ledger", "to": "/job-work/ledger"},
      {"label": "Landed Cost", "to": "/procurement/landed-cost"},
    ] },
    { label: "Backup", items: [
      {"label": "Dispatch Money Approvals", "to": "/finance/dispatch-approvals"},
    ] },
    { label: "View only", items: [
      {"label": "Stock", "to": "/inventory", "readOnly": true},
    ] },
    { label: "Masters", items: [
      {"label": "Raw Materials", "to": "/raw-materials"},
      {"label": "BOMs", "to": "/manufacturing/boms"},
    ] },
  ],
  P06: [
    { label: "Money Gate", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "Dispatch Approvals", "to": "/finance/dispatch-approvals"},
      {"label": "Holds & Approvals", "to": "/finance/approvals"},
      {"label": "Payment Confirmations", "to": "/payment-confirmations"},
      // 2026-09-28 SS round — Admin OR Accounts decide confirmed-order edit/delete requests.
      {"label": "Order changes", "to": "/approvals/order-changes"},
      {"label": "TA/DA Claims", "to": "/finance/tada-claims"},
    ] },
    { label: "Books", items: [
      {"label": "Dealer Ledger", "to": "/dealer-ledger"},
      {"label": "Ledger", "to": "/accounting?tab=ledger"},
      {"label": "Vouchers", "to": "/ledger/vouchers"},
      {"label": "Invoices", "to": "/invoices"},
      {"label": "Purchase Invoices", "to": "/purchase-invoices"},
    ] },
    { label: "Reports", items: [
      {"label": "Receivables & Payables", "to": "/accounting?tab=outstanding"},
      {"label": "P&L", "to": "/ledger/profit-loss"},
      {"label": "Balance Sheet", "to": "/ledger/balance-sheet"},
    ] },
    { label: "Compliance", items: [
      {"label": "GST Filing", "to": "/gst/filing"},
      {"label": "e-Invoice", "to": "/gst/e-invoices"},
      {"label": "e-Way Bill", "to": "/gst/e-waybills"},
    ] },
    { label: "View only", items: [
      {"label": "Dispatch", "to": "/dispatch-status", "readOnly": true},
      {"label": "Store", "to": "/inventory", "readOnly": true},
    ] },
    { label: "Masters", items: [
      {"label": "Raw Materials", "to": "/raw-materials"},
      {"label": "BOMs", "to": "/manufacturing/boms"},
    ] },
  ],
  P07: [
    { label: "Shop Floor", items: [
      {"label": "Dashboard", "to": "/manufacturing/dashboard"},
      {"label": "Production Orders", "to": "/production"},
      {"label": "Work Centres", "to": "/manufacturing/process-kanban"},
      {"label": "Shift Control", "to": "/manufacturing/shift-control"},
      {"label": "Shortages", "to": "/manufacturing/shortages"},
    ] },
    { label: "Quality handoff", items: [
      {"label": "QC Handoff", "to": "/manufacturing/qc"},
      {"label": "Rejections", "to": "/rejections"},
    ] },
    { label: "Inputs", items: [
      {"label": "Materials", "to": "/inventory", "readOnly": true},
      {"label": "Raw Materials", "to": "/raw-materials"},
      {"label": "BOMs", "to": "/manufacturing/boms"},
      {"label": "Shift Reports", "to": "/manufacturing/shift-report"},
      {"label": "Scrap & Wastage", "to": "/manufacturing/scrap"},
    ] },
  ],
  P08: [
    { label: "My Shift", items: [
      {"label": "My Shift", "to": "/my-shift"},
      {"label": "My Jobs", "to": "/my-shift/tasks"},
      {"label": "Machine Check", "to": "/my-shift/machine-check"},
      {"label": "Readings", "to": "/my-shift/readings"},
      {"label": "Move Units", "to": "/my-shift/move"},
      {"label": "Report Problem", "to": "/my-shift/problem"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P09: [
    { label: "My Shift", items: [
      {"label": "My Shift", "to": "/my-shift"},
      {"label": "My Jobs", "to": "/my-shift/tasks"},
      {"label": "Finishing Checklist", "to": "/my-shift/checklist"},
      {"label": "Required Photos", "to": "/my-shift/photos"},
      {"label": "Move Units", "to": "/my-shift/move"},
      {"label": "Report Problem", "to": "/my-shift/problem"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P10: [
    { label: "Quality", items: [
      {"label": "Dashboard", "to": "/quality"},
      {"label": "Lot Approvals", "to": "/manufacturing/lot-approvals"},
      {"label": "Incoming (IQC)", "to": "/manufacturing/rm-inspection"},
      {"label": "Disposition", "to": "/quality/disposition"},
      {"label": "Production Release", "to": "/quality/release"},
      {"label": "Count Approvals", "to": "/quality/count-approvals"},
      {"label": "Escalations", "to": "/quality/escalations"},
    ] },
    { label: "Records", items: [
      {"label": "Readings Log", "to": "/manufacturing/floor-inspection"},
      {"label": "Rejections", "to": "/rejections"},
      {"label": "Instruments", "to": "/manufacturing/instruments"},
    ] },
    { label: "Masters (controlled)", items: [
      {"label": "QC Templates", "to": "/qc-templates"},
      {"label": "Raw Materials", "to": "/raw-materials"},
      {"label": "BOMs", "to": "/manufacturing/boms"},
    ] },
  ],
  P11: [
    { label: "Inspections", items: [
      {"label": "My Inspections", "to": "/inspect"},
      {"label": "Queue", "to": "/inspect/queue"},
      {"label": "Take Readings", "to": "/inspect/readings"},
      {"label": "Scan", "to": "/inspect/scan"},
      {"label": "My Escalations", "to": "/inspect/escalations"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P12: [
    { label: "Dispatch", items: [
      {"label": "Dashboard", "to": "/warehouse/dispatch-dashboard"},
      {"label": "Dispatch Forms", "to": "/dispatch-forms"},
      {"label": "Counts & Shortages", "to": "/dispatch/shortages"},
      {"label": "Loading Progress", "to": "/dispatch/loading"},
      {"label": "Shipments", "to": "/dispatch/shipments"},
      {"label": "Accounts Status", "to": "/dispatch/account-status"},
    ] },
    { label: "Service desk", items: [
      {"label": "Service Tickets", "to": "/service-tickets"},
      {"label": "Warranties", "to": "/warranties"},
    ] },
    { label: "View only", items: [
      {"label": "Gate Passes", "to": "/warehouse/gate-passes", "readOnly": true},
      {"label": "Inventory", "to": "/inventory", "readOnly": true},
    ] },
  ],
  P13: [
    { label: "Loading", items: [
      {"label": "My Load", "to": "/loading"},
      {"label": "Assigned Loads", "to": "/loads"},
      {"label": "Scan Item", "to": "/loading/scan"},
      {"label": "Layer Photos", "to": "/loading/photos"},
      {"label": "Report Damage", "to": "/loading/damage"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P14: [
    { label: "Stock", items: [
      {"label": "Dashboard", "to": "/warehouse/dashboard"},
      {"label": "Stock", "to": "/inventory"},
      {"label": "Batches", "to": "/warehouse/product-units"},
    ] },
    { label: "Movements", items: [
      {"label": "GRN / Inward", "to": "/warehouse/inward"},
      {"label": "Putaway", "to": "/warehouse/putaway"},
      {"label": "Issue to Production", "to": "/warehouse/issue"},
      {"label": "Transfer", "to": "/warehouse/transfers"},
      {"label": "Job-work Challans", "to": "/job-work"},
      {"label": "Scrap", "to": "/manufacturing/scrap"},
    ] },
    { label: "Warehouse", items: [
      {"label": "Bins", "to": "/warehouse-locations"},
      {"label": "Cycle Count", "to": "/warehouse/stock-audit"},
    ] },
    { label: "Masters", items: [
      {"label": "Raw Materials", "to": "/raw-materials"},
      {"label": "BOMs", "to": "/manufacturing/boms"},
    ] },
  ],
  P15: [
    { label: "Gate", items: [
      {"label": "Gate Dashboard", "to": "/gate"},
      {"label": "Gate Passes", "to": "/gate/queue"},
      {"label": "Scan In", "to": "/gate/scan-in"},
      {"label": "Scan Out", "to": "/gate/scan-out"},
      {"label": "Today's Log", "to": "/gate/log"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P16: [
    { label: "My Service Day", items: [
      {"label": "My Day", "to": "/service/my-day"},
      {"label": "My Tickets", "to": "/service/my-tickets"},
      {"label": "Installations", "to": "/service/installations"},
      {"label": "Warranty Lookup", "to": "/warranty-lookup"},
      {"label": "Parts in Bag", "to": "/service/parts"},
    ] },
    { label: "Me", items: [
      {"label": "My Profile", "to": "/profile"},
    ] },
  ],
  P17: [
    { label: "People", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "People", "to": "/employees"},
      {"label": "Attendance", "to": "/attendance-admin"},
      {"label": "Team Tracking", "to": "/team-tracking"},
      {"label": "Leave", "to": "/leave-requests"},
      {"label": "Expense Approvals", "to": "/expense-approvals"},
    ] },
    { label: "Pay", items: [
      {"label": "Payroll", "to": "/payroll"},
      {"label": "Incentives", "to": "/incentives"},
    ] },
    { label: "Hiring", items: [
      {"label": "Recruitment", "to": "/hr/recruitment"},
    ] },
  ],
  P18: [
    { label: "Overview", items: [
      {"label": "Dashboard", "to": "/dashboard"},
      {"label": "AI Insights", "to": "/ai"},
    ] },
    { label: "Performance", items: [
      {"label": "Oversight", "to": "/scorecard/org"},
      {"label": "Pipeline", "to": "/pipeline"},
      {"label": "Sales Targets", "to": "/admin/sales-targets"},
      {"label": "Team Performance", "to": "/admin/team-performance"},
      {"label": "Team Tracking", "to": "/team-tracking"},
    ] },
    { label: "Revenue", items: [
      {"label": "Leads", "to": "/leads"},
      {"label": "Proposals", "to": "/proposals"},
      {"label": "Dealers", "to": "/dealer-management"},
      {"label": "Prospect dealers", "to": "/dealer-prospects"},
      {"label": "Invoices", "to": "/invoices"},
    ] },
    // (2026-09-18, Manak) REQUESTS — the two approval queues an admin is asked
    // to action, together. "Discount Approvals" used to sit in Revenue between
    // Proposals and Dealers, reading as reporting rather than a to-do, and
    // ACCESS REQUESTS WAS MISSING FROM THIS PROFILE ENTIRELY — the only entry
    // lived in sidebarRegistry, which this profile never reaches (Sidebar.tsx
    // returns profileMenu(profile) and ignores the registry when a profile
    // resolves; role 'admin' resolves to P18). That is why an administrator had
    // no way to reach the dealer access requests from the sidebar.
    //
    // /admin/access-requests is ONE page serving BOTH queues: lead/customer
    // access + reassignment requests, and the dealer access requests reps raise
    // from the app (DealerAccessRequestsSection).
    //
    // Label is "Discount Requests" to match the route, the page and P02's
    // wording; "Discount Approvals" was the odd one out.
    { label: "Requests", items: [
      {"label": "Discount Requests", "to": "/discount-requests"},
      // 2026-09-28 SS round — confirmed sales order edit/delete requests.
      {"label": "Order changes", "to": "/approvals/order-changes"},
      {"label": "Access Requests", "to": "/admin/access-requests"},
      {"label": "TA/DA Claims", "to": "/finance/tada-claims"},
      {"label": "TA/DA Rates", "to": "/admin/field-ta-da-rates"},
    ] },
    { label: "Operations", items: [
      {"label": "Orders", "to": "/sales-orders"},
      {"label": "Production", "to": "/manufacturing/dashboard"},
      {"label": "Quality", "to": "/manufacturing/lot-approvals"},
      {"label": "Inventory", "to": "/inventory"},
      {"label": "Dispatch", "to": "/warehouse/dispatch-dashboard"},
      {"label": "Gate Passes", "to": "/warehouse/gate-passes"},
      {"label": "Service", "to": "/service-tickets"},
    ] },
    { label: "Procurement", items: [
      {"label": "Purchase", "to": "/purchase-dashboard"},
      {"label": "Vendors", "to": "/vendors"},
    ] },
    { label: "Books", items: [
      {"label": "Dealer Ledger", "to": "/dealer-ledger"},
      {"label": "Dispatch Approvals", "to": "/finance/dispatch-approvals"},
      {"label": "Profit & Loss", "to": "/ledger/profit-loss"},
      {"label": "Outstanding", "to": "/accounting?tab=outstanding"},
      {"label": "GST Filing", "to": "/gst/filing"},
    ] },
    { label: "Org", items: [
      {"label": "People", "to": "/employees"},
      {"label": "Attendance", "to": "/attendance-admin"},
      {"label": "Roles & Access", "to": "/admin/roles"},
      {"label": "Discount Policies", "to": "/admin/discount-policies"},
      {"label": "Audit Logs", "to": "/admin/audit-logs"},
      {"label": "AURA Settings", "to": "/admin/settings"},
    ] },
    HELP_SECTION,
  ],
};

export function profileMenu(profile: ProfileId): ProfileMenuSection[] { return MENUS[profile]; }
